import { checkReportRange, typesOfGroups, type EventRow, type ReportResult } from "@fts/core";
import { PageHeader, Panel } from "../../components/ui";
import { useI18n } from "../../i18n";
import { alarmTypeLabel } from "../../lib/alarms";
import { formatDateTimeIn } from "../../lib/export/sheet";
import { eventsSheet } from "../../lib/report-sheets";
import type { Route } from "../../router/hash";
import { useSession, type TenantData } from "../../state/session";
import { ExportBar, FilterBar, Loading, ReportMeta, filterSummary, parseFilters, rangeKey, useLoad, type ExportSpec } from "./shared";

/** Olay raporu (ADR 0018): alarms of the scope with type, time, vehicle, operator, site, severity, acknowledgement. */
export function EventsReport({ data, route }: { data: TenantData; route: Route }) {
  const { t, lang } = useI18n();
  const { provider, tenant } = useSession();
  const f = parseFilters(route, data);
  const issue = checkReportRange(f.from, f.to);
  const tenantId = tenant?.id;
  const types = typesOfGroups(f.groups);
  const query = (id: string) => ({ tenantId: id, siteId: f.siteId, vehicleIds: f.vehicleIds, operatorId: f.operatorId, from: f.from, to: f.to, types });
  const state = useLoad<ReportResult<EventRow>>(
    tenantId && !issue ? () => provider.reportEvents(query(tenantId)) : null,
    [provider, tenantId, f.siteId, f.vehicleIds.join(","), f.operatorId, f.from, f.to, types.join(",")],
  );
  const rows = state.data?.rows ?? [];
  const tz = state.data?.tz ?? "Europe/Istanbul";
  const withOperators = rows.some((r) => r.operatorName !== null);
  const spec: ExportSpec | null = state.status === "ready" && state.data && tenantId
    ? {
        report: "events",
        reportName: t("reports.events.title"),
        slug: t("reports.events.slug"),
        part: `${f.from}_${f.to}`,
        tz,
        filters: filterSummary(t, data, f, { groups: true }),
        rows: rows.length,
        load: async (format) => {
          const r = await provider.exportEvents(query(tenantId), format);
          return { sheets: [eventsSheet(t, r.rows, r.rows.some((x) => x.operatorName !== null))], tz: r.tz };
        },
      }
    : null;
  const rk = rangeKey(issue);

  return (
    <>
      <PageHeader title={t("reports.events.title")} subtitle={t("reports.events.subtitle")} eyebrow={t("nav.group.reports")} />
      <FilterBar data={data} path="/reports/events" filters={f} groups />
      {rk && (
        <p className="form-error" role="alert">
          {t(rk)}
        </p>
      )}
      <ReportMeta tz={state.data?.tz ?? null} sample={provider.mode === "demo"} />
      <ExportBar spec={spec} disabled={rows.length === 0} />
      {state.status === "error" && (
        <p className="form-error" role="alert">
          {state.error}
        </p>
      )}
      {state.data?.truncated && <p className="form-error">{t("reports.truncated", { max: rows.length })}</p>}
      <Panel flush meta={state.status === "ready" ? <span>{t("reports.rows", { count: rows.length })}</span> : undefined}>
        {state.status !== "ready" && state.status !== "error" ? (
          rk ? <p className="table-empty">{t(rk)}</p> : <Loading />
        ) : rows.length === 0 ? (
          <p className="table-empty">{t("reports.noData")}</p>
        ) : (
          <div className="table-wrap">
            <table className="table report-table">
              <thead>
                <tr>
                  <th scope="col">{t("reports.col.time")}</th>
                  <th scope="col">{t("reports.col.type")}</th>
                  <th scope="col">{t("reports.col.vehicle")}</th>
                  <th scope="col" className="col-hide-sm">{t("reports.col.site")}</th>
                  {withOperators && <th scope="col" className="col-hide-md">{t("reports.col.operator")}</th>}
                  <th scope="col">{t("reports.col.status")}</th>
                  <th scope="col" className="col-hide-md">{t("reports.col.ackNote")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className="nowrap">
                      <a className="row-link" href={`#/alarms/${encodeURIComponent(r.id)}`}>
                        {formatDateTimeIn(r.occurredAt, tz, lang)}
                      </a>
                    </td>
                    <td>
                      {alarmTypeLabel(t, r.type)}
                      {r.type === "impact" && <span className="muted"> · {t("reports.severity", { level: r.severity })}</span>}
                      {r.geofenceName && <span className="muted"> · {r.geofenceName}</span>}
                    </td>
                    <td className="mono">{r.fleetNo ?? t("common.empty")}</td>
                    <td className="col-hide-sm">{r.siteName ?? t("common.empty")}</td>
                    {withOperators && <td className="col-hide-md">{r.operatorName ?? t("common.empty")}</td>}
                    <td>
                      <span className={`badge tone-${r.status === "open" ? "crit" : "ok"}`}>{r.status === "open" ? t("reports.status.open") : t("reports.status.acknowledged")}</span>
                    </td>
                    <td className="col-hide-md">{r.ackNote ?? t("common.empty")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      <p className="panel-footnote">{t("reports.events.rules")}</p>
    </>
  );
}
